
import Vue from 'vue';
import HeaderLogo from './HeaderLogo.vue';
import HeaderLogoAdditional from './HeaderLogoAdditional.vue';
import HeaderMenu from './HeaderMenu.vue';
import HeaderActions from './HeaderActions.vue';
import HeaderCustomerMenu from '@root/common/components/layout/header/HeaderCustomerMenu.vue';
import PianoCustomerStateHandler from '@piano/components/PianoCustomerStateHandler.vue';
import { Header, HeaderActions as IHeaderActions } from '@root/modules/navigation/types/navigation';
import { Data as CategoriesData } from '@root/modules/category/types/categories';
import MenuLightbox from '@root/common/components/base/MenuLightbox.vue';
import { faArrowLeft } from '@delfi.lt/fa-kit/icons/modules/classic/regular';
import isMobileView from '@root/common/utils/isMobileView';

const HeaderSitemap = () => import(/* webpackChunkName: "header/HeaderSitemap" */ './HeaderSitemap.vue');
const HeaderSearch = () => import(/* webpackChunkName: "header/HeaderSearch" */ './HeaderSearch.vue');

interface Props {
  navigation: Header;
  hideHeaderMenu: boolean;
  isEasyChannel: boolean;
}

interface Data {
  isSticky: boolean;
  faArrowLeft: typeof faArrowLeft;
}

interface Methods {
  animateHeader: () => void;
  updateActionEvent: (type: IHeaderActions['type'], value: IHeaderActions['value']) => void;
  controlBodyOverflow: (isSitemapOpen: boolean) => void;
  handleOverflow: () => void;
  refetchFrontpage: () => void;
  navigateToHomepage: () => void;
}

interface Computed {
  siteLogo: {
    src: {
      desktop: string | null;
      mobile: string | null;
    };
    url: string | null;
    alt: string;
    logoIsAnimated: boolean;
    logoSize: {
      desktop: {
        width: number;
        height: number;
      };
      mobile: {
        width: number;
        height: number;
      };
    };
  };
  isSearchOpen: boolean;
  isSitemapOpen: boolean;
  isProfileInfoOpen: boolean;
  isFrontpage: boolean;
  categoryData: CategoriesData['parentCategory'] | null;
  activeCategoryName: string | null;
  isMobileView: boolean;
  isNonDelfi: boolean;
  unreadCount: number;
  showProfileDot: boolean;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  name: 'Header',
  components: {
    HeaderLogo,
    HeaderLogoAdditional,
    HeaderMenu,
    HeaderActions,
    HeaderSearch,
    HeaderSitemap,
    HeaderCustomerMenu,
    PianoCustomerStateHandler,
    MenuLightbox,
  },
  props: {
    navigation: {
      type: Object,
      required: true,
    },
    hideHeaderMenu: {
      type: Boolean,
      required: true,
    },
    isEasyChannel: {
      type: Boolean,
      default: false,
      required: true,
    },
  },
  data() {
    return {
      isSticky: false,
      faArrowLeft,
    };
  },
  computed: {
    unreadCount() {
      return this.$store.state.notification.unreadCount ?? 0;
    },
    showProfileDot() {
      return this.$store.state.notification.showProfileDot ?? false;
    },
    siteLogo() {
      const header = this.$channelConfig('header');
      const name = this.$channelConfig('settings').name;
      let customCategoryHeaderLogo = null;

      if (this.$route && this.$route.params.category) {
        const categorySlug = this.$route.params.subcategory || this.$route.params.category;
        const customCategorySettings = this.$channelConfig('page').category?.component?.header;
        if (customCategorySettings) {
          if (Array.isArray(customCategorySettings)) {
            const categoryAdditionalData =
              customCategorySettings.find((item: { categories: string | string[] }) => item.categories.includes(categorySlug)) || null;
            customCategoryHeaderLogo = categoryAdditionalData?.logo || null;
          }
        }
      }

      return {
        src: {
          desktop: customCategoryHeaderLogo || header?.logo || null,
          mobile: customCategoryHeaderLogo || header?.logoMobile || null,
        },
        url: header.logoUrl,
        alt: name,
        logoIsAnimated: !!header.logoIsAnimated,
        logoSize: header.logoSize,
      };
    },
    isSearchOpen() {
      return this.$store.getters['navigation/getHeaderAction']('search');
    },
    isSitemapOpen() {
      return this.$store.getters['navigation/getHeaderAction']('sitemap');
    },
    isProfileInfoOpen() {
      return this.$store.getters['navigation/getHeaderAction']('profileInfo');
    },
    isFrontpage() {
      const id = this.$channelConfig('settings').id;

      return this.$route.name === 'frontpage' || (this.$route.name === 'channel' && ['ru', 'en'].includes(id));
    },
    categoryData() {
      const category = this.$store.getters['category/getCategoryData'];
      return category?.parentCategory?.id ? category?.parentCategory : category;
    },
    activeCategoryName() {
      const id = this.$channelConfig('settings').id;
      return this.navigation.bottom?.parentSlug?.toLowerCase() || this.categoryData?.name?.toLowerCase() || id || null;
    },
    isMobileView() {
      return isMobileView();
    },
    isNonDelfi() {
      const id = this.$channelConfig('settings').id;

      return ['fone', 'm360', 'tvarilietuva', 'zinios-visiems'].includes(id);
    },
  },
  watch: {
    isSitemapOpen(value: boolean) {
      this.controlBodyOverflow(value);
    },
    $route() {
      this.updateActionEvent('sitemap', false);
      this.updateActionEvent('profileInfo', false);
    },
  },
  mounted() {
    // Disable header sticky in Internet Explorer
    if (this.$ua.browser() !== 'Internet Explorer') {
      this.animateHeader();
      window.addEventListener('scroll', this.animateHeader);
      window.addEventListener('resize', this.animateHeader);
    }
  },
  destroyed() {
    if (this.$ua.browser() !== 'Internet Explorer') {
      window.removeEventListener('scroll', this.animateHeader);
      window.removeEventListener('resize', this.animateHeader);
    }
  },
  methods: {
    animateHeader() {
      const $menuTop: Element | null = document.querySelector('.header-menu--top');
      const stickyThreshold = this.$channelConfig('settings').name === 'multimedija' || this.$channelConfig('settings').name === 'multimedia' ? 0 : 30;
      const menuTopHeight: number = $menuTop?.clientHeight || stickyThreshold;
      const header = this.$refs.header as Element;

      if (!isMobileView()) {
        const scroll: number = window.pageYOffset;
        // Make header sticky after scrolling down $menuTop height
        this.isSticky = scroll >= menuTopHeight;
      } else {
        this.isSticky = true;
      }

      // Set delay of header height definition caused by css transition
      setTimeout(() => {
        document.documentElement.style.setProperty('--delfi-header-height', `${header.clientHeight}px`);
      }, 305);
    },
    updateActionEvent(type, value) {
      // Add no-scroll when opening search
      if (type === 'search') {
        if (value === true) {
          document.body.classList.add('no-scroll');
        }
      }

      // Remove no-scroll when closing all overlays
      if (type === 'all' && !value) {
        document.body.classList.remove('no-scroll');
      }

      if (type !== 'all') {
        this.$store.commit('navigation/setHeaderAction', { type, value });
        return;
      }

      this.$store.commit('navigation/setHeaderAction', { type: 'sitemap', value });
      this.$store.commit('navigation/setHeaderAction', { type: 'search', value });
    },
    handleOverflow() {
      // Hide body overflow if sitemap is open on smaller devices
      const changeOverflow = !isMobileView() && this.isSitemapOpen ? 'add' : 'remove';
      document.body.classList[changeOverflow]('overflow-hidden');
    },
    controlBodyOverflow(isSitemapOpen) {
      const changeEventListener = isSitemapOpen ? 'addEventListener' : 'removeEventListener';

      this.handleOverflow();
      window[changeEventListener]('resize', this.handleOverflow);
    },
    refetchFrontpage() {
      if (this.$route.name === 'frontpage') {
        this.$store.commit('frontpage/setRefetchState', true);
      } else {
        this.$router.push('/');
      }
    },
    navigateToHomepage() {
      this.$event.send('link-click', { name: 'header-home-button', type: `header-actions` });
      location.href = '/';
    },
  },
});
